import * as turf from "@turf/turf";
import { STATIONS, ZONE_RADIUS_MILES } from "./stations";
import type { Station } from "./types";

/**
 * Pure elimination logic. Each function returns the ids of stations that the
 * answer rules out. Nothing here touches the map or store, so it is trivially
 * testable and reused identically across local + synced play.
 *
 * Zone-aware mode (default) never eliminates a station unless its ENTIRE
 * ¼-mile zone fails the test — modeled as a ±0.25mi buffer on the center
 * distance. "strict" compares the station center only.
 */

export type LngLat = [number, number];
// Effective hiding-zone radius used for zone-aware buffering. Updated when the
// gamemaster changes the zone mode (block zones use a smaller buffer).
let R = ZONE_RADIUS_MILES;
export function setZoneRadius(mi: number) { R = mi; }

function miles(a: LngLat, b: LngLat): number {
  return turf.distance(a, b, { units: "miles" });
}

function center(s: Station): LngLat {
  return [s.lng, s.lat];
}

// ---- Radar: "Are you within D miles of me?" -------------------------------
export function radarEliminate(
  seeker: LngLat,
  distanceMiles: number,
  answer: "yes" | "no",
  strict = false
): string[] {
  const out: string[] = [];
  for (const s of STATIONS) {
    const d = miles(center(s), seeker);
    let eliminate: boolean;
    if (answer === "yes") {
      // Hider is within D. Rule out stations whose whole zone is beyond D.
      eliminate = strict ? d > distanceMiles : d - R > distanceMiles;
    } else {
      // Hider is NOT within D. Rule out stations whose whole zone is inside D.
      eliminate = strict ? d <= distanceMiles : d + R <= distanceMiles;
    }
    if (eliminate) out.push(s.id);
  }
  return out;
}

// ---- Thermometer: traveled A->B, "hotter or colder?" ----------------------
// "hotter" => seeker got closer to the hider => hider is on B's side of the
// perpendicular bisector of A--B. "colder" => A's side.
export function thermometerEliminate(
  a: LngLat,
  b: LngLat,
  answer: "hotter" | "colder",
  strict = false
): string[] {
  const bisector = bisectorLine(a, b);
  const out: string[] = [];
  for (const s of STATIONS) {
    const c = center(s);
    const onBSide = miles(c, b) < miles(c, a);
    const onWrongSide = answer === "hotter" ? !onBSide : onBSide;
    if (!onWrongSide) continue;
    if (strict) {
      out.push(s.id);
    } else {
      // Only eliminate if the whole zone is on the wrong side.
      const distToLine = turf.pointToLineDistance(c, bisector, { units: "miles" });
      if (distToLine > R) out.push(s.id);
    }
  }
  return out;
}

/** Long perpendicular bisector of segment A--B as a LineString. */
function bisectorLine(a: LngLat, b: LngLat) {
  const mid = turf.midpoint(a, b).geometry.coordinates as LngLat;
  const bearingAB = turf.bearing(a, b);
  const perp = bearingAB + 90;
  const far = 50; // miles — long enough to span Manhattan
  const p1 = turf.destination(mid, far, perp, { units: "miles" }).geometry.coordinates as LngLat;
  const p2 = turf.destination(mid, far, perp + 180, { units: "miles" }).geometry.coordinates as LngLat;
  return turf.lineString([p1, p2]);
}

// ---- Manual cut: draw a line, eliminate one side --------------------------
// Smart-orients: a more-horizontal line offers above/below; a more-vertical
// line offers left/right.
export function cutOptions(a: LngLat, b: LngLat): ("above" | "below" | "left" | "right")[] {
  return Math.abs(b[0] - a[0]) >= Math.abs(b[1] - a[1]) ? ["above", "below"] : ["left", "right"];
}

function extendedLine(a: LngLat, b: LngLat) {
  const brg = turf.bearing(a, b);
  const p1 = turf.destination(a, 60, brg + 180, { units: "miles" }).geometry.coordinates as LngLat;
  const p2 = turf.destination(b, 60, brg, { units: "miles" }).geometry.coordinates as LngLat;
  return turf.lineString([p1, p2]);
}

export function halfPlaneEliminate(
  a: LngLat,
  b: LngLat,
  side: "above" | "below" | "left" | "right",
  strict = false
): string[] {
  const line = extendedLine(a, b);
  const out: string[] = [];
  for (const s of STATIONS) {
    const c = center(s);
    let onEliminateSide: boolean;
    if (side === "above" || side === "below") {
      const latAt = a[1] + (b[1] - a[1]) * ((c[0] - a[0]) / ((b[0] - a[0]) || 1e-9));
      onEliminateSide = side === "above" ? c[1] > latAt : c[1] < latAt;
    } else {
      const lngAt = a[0] + (b[0] - a[0]) * ((c[1] - a[1]) / ((b[1] - a[1]) || 1e-9));
      onEliminateSide = side === "right" ? c[0] > lngAt : c[0] < lngAt;
    }
    if (!onEliminateSide) continue;
    if (strict || turf.pointToLineDistance(c, line, { units: "miles" }) > R) out.push(s.id);
  }
  return out;
}

/** The extended cut line as a geometry, for previewing on the map. */
export function cutLine(a: LngLat, b: LngLat): GeoJSON.Feature {
  return extendedLine(a, b);
}

/**
 * A freehand open stroke splits the map into two regions. Returns [sideA, sideB]
 * polygons: the drawn path is extended far past its ends and closed off to the
 * left / right, so each polygon covers one side of the curve.
 */
export function freehandSlicePolys(pts: LngLat[]): [GeoJSON.Feature<GeoJSON.Polygon>, GeoJSON.Feature<GeoJSON.Polygon>] {
  const a = pts[0], b = pts[pts.length - 1];
  const dir = turf.bearing(a, b);
  const L = 60;
  const ext = (p: LngLat, bearing: number) => turf.destination(p, L, bearing, { units: "miles" }).geometry.coordinates as LngLat;
  const extA = ext(a, dir + 180), extB = ext(b, dir);
  const base = [extA, ...pts, extB];
  const sideA = turf.polygon([[...base, ext(extB, dir - 90), ext(extA, dir - 90), extA]]);
  const sideB = turf.polygon([[...base, ext(extB, dir + 90), ext(extA, dir + 90), extA]]);
  return [sideA, sideB];
}

// ---- Admin region (congressional / community-board / neighborhood) --------
// Whole-district select: eliminate the stations inside a district, or keep only
// them (isolate). Center-in-polygon, robust to Polygon AND MultiPolygon.
export function regionEliminate(
  geometry: GeoJSON.Geometry,
  mode: "keep-inside" | "eliminate-inside"
): string[] {
  const poly = geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
  const out: string[] = [];
  for (const s of STATIONS) {
    let inside = false;
    try { inside = turf.booleanPointInPolygon(turf.point(center(s)), poly); } catch { inside = false; }
    if (mode === "eliminate-inside" ? inside : !inside) out.push(s.id);
  }
  return out;
}

// ---- Region paint: keep-only-inside / eliminate-inside --------------------
export function polygonEliminate(
  polygon: GeoJSON.Feature<GeoJSON.Polygon> | GeoJSON.Polygon,
  mode: "keep-inside" | "eliminate-inside",
  strict = false
): string[] {
  const poly = (polygon.type === "Feature" ? polygon : turf.feature(polygon)) as GeoJSON.Feature<GeoJSON.Polygon>;
  // Simple single-ring polygons (what the paint tool draws) yield a LineString.
  const ring = turf.polygonToLine(poly) as GeoJSON.Feature<GeoJSON.LineString>;
  const out: string[] = [];
  for (const s of STATIONS) {
    const c = turf.point(center(s));
    const inside = turf.booleanPointInPolygon(c, poly);
    const distToEdge = turf.pointToLineDistance(c, ring, { units: "miles" });
    if (mode === "keep-inside") {
      // Eliminate stations whose whole zone is outside the kept region.
      const wholeOutside = strict ? !inside : !inside && distToEdge > R;
      if (wholeOutside) out.push(s.id);
    } else {
      // Eliminate stations whose whole zone is inside the painted region.
      const wholeInside = strict ? inside : inside && distToEdge > R;
      if (wholeInside) out.push(s.id);
    }
  }
  return out;
}
